import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useTeam } from '../../../context/TeamContext';
import { useNotification } from '../../../context/NotificationContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';

const NurseBottomNavigation = () => {
  const location = useLocation();
  const { currentUser } = useAuth();
  const { teams } = useTeam();
  const { getEquipmentPendingActionsCount } = useNotification();
  const [shiftsBadgeCount, setShiftsBadgeCount] = useState(0);
  const [equipmentBadgeCount, setEquipmentBadgeCount] = useState(0);

  // Load badge counts from all teams
  useEffect(() => {
    if (!currentUser || !teams || teams.length === 0 || !getEquipmentPendingActionsCount) {
      setShiftsBadgeCount(0);
      setEquipmentBadgeCount(0);
      return;
    }

    const loadBadgeCounts = async () => {
      try {
        let totalShiftsCount = 0;
        let totalEquipmentCount = 0;

        // Loop through all teams
        for (const team of teams) {
          const userRole = team.userRole || 'MEMBER';
          
          // Get equipment pending actions count for this team
          try {
            const equipmentCount = await getEquipmentPendingActionsCount(
              userRole,
              team.id,
              currentUser.uid
            );
            totalEquipmentCount += equipmentCount;
          } catch (err) {
            console.error(`Error loading equipment count for team ${team.id}:`, err);
          }

          // Get shifts pending actions count for this team
          // Count shift swaps that need approval
          try {
            if (userRole === 'LEADER' || userRole === 'DEPUTY_LEADER') {
              // For leaders: Count pending shift swap requests
              const swapRequestsQuery = query(
                collection(db, 'shiftSwaps'),
                where('teamId', '==', team.id),
                where('status', '==', 'PENDING')
              );
              const swapRequestsSnapshot = await getDocs(swapRequestsQuery);
              totalShiftsCount += swapRequestsSnapshot.size;
            } else {
              // For members: Count their own pending swap requests
              const swapRequestsQuery = query(
                collection(db, 'shiftSwaps'),
                where('teamId', '==', team.id),
                where('requestedBy', '==', currentUser.uid),
                where('status', '==', 'PENDING')
              );
              const swapRequestsSnapshot = await getDocs(swapRequestsQuery);
              totalShiftsCount += swapRequestsSnapshot.size;
            }
          } catch (err) {
            console.error(`Error loading shifts count for team ${team.id}:`, err);
          }
        }

        setShiftsBadgeCount(totalShiftsCount);
        setEquipmentBadgeCount(totalEquipmentCount);
      } catch (err) {
        console.error('Error loading badge counts:', err);
        setShiftsBadgeCount(0);
        setEquipmentBadgeCount(0);
      }
    };

    loadBadgeCounts();
  }, [currentUser, teams, getEquipmentPendingActionsCount]);

  const isActive = (path) => {
    if (path === '/nurse' || path === '/nurse/home') {
      return location.pathname === '/nurse' || location.pathname === '/nurse/home';
    }
    if (path === '/nurse/shifts') {
      return location.pathname.startsWith('/nurse/shifts');
    }
    if (path === '/nurse/equipment') {
      return location.pathname.startsWith('/nurse/equipment');
    }
    if (path === '/nurse/profile') {
      return location.pathname.startsWith('/nurse/profile');
    }
    return false;
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
      <div className="container mx-auto">
        <div className="flex items-center justify-around py-2">
          {/* หน้าแรก */}
          <Link
            to="/nurse"
            className={`flex flex-col items-center justify-center px-4 py-2 transition ${
              isActive('/nurse') || isActive('/nurse/home')
                ? 'text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <svg
              className={`w-6 h-6 mb-1 ${isActive('/nurse') || isActive('/nurse/home') ? 'text-blue-600' : ''}`}
              fill={isActive('/nurse') || isActive('/nurse/home') ? 'currentColor' : 'none'}
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
            </svg>
            <span className="text-xs font-medium">หน้าแรก</span>
          </Link>

          {/* ตารางเวร */}
          <Link
            to="/nurse/shifts"
            className={`flex flex-col items-center justify-center px-4 py-2 transition relative ${
              isActive('/nurse/shifts')
                ? 'text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="relative">
              <svg
                className={`w-6 h-6 mb-1 ${isActive('/nurse/shifts') ? 'text-blue-600' : ''}`}
                fill={isActive('/nurse/shifts') ? 'currentColor' : 'none'}
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              {shiftsBadgeCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-4.5 flex items-center justify-center px-1 border-2 border-white">
                  {shiftsBadgeCount > 9 ? '9+' : shiftsBadgeCount}
                </span>
              )}
            </div>
            <span className="text-xs font-medium">ตารางเวร</span>
          </Link>

          {/* อุปกรณ์ */}
          <Link
            to="/nurse/equipment"
            className={`flex flex-col items-center justify-center px-4 py-2 transition relative ${
              isActive('/nurse/equipment')
                ? 'text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="relative">
              <svg
                className={`w-6 h-6 mb-1 ${isActive('/nurse/equipment') ? 'text-blue-600' : ''}`}
                fill={isActive('/nurse/equipment') ? 'currentColor' : 'none'}
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                />
              </svg>
              {equipmentBadgeCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-4.5 flex items-center justify-center px-1 border-2 border-white">
                  {equipmentBadgeCount > 9 ? '9+' : equipmentBadgeCount}
                </span>
              )}
            </div>
            <span className="text-xs font-medium">อุปกรณ์</span>
          </Link>

          {/* Profile */}
          <Link
            to="/nurse/profile"
            className={`flex flex-col items-center justify-center px-4 py-2 transition ${
              isActive('/nurse/profile')
                ? 'text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <svg
              className={`w-6 h-6 mb-1 ${isActive('/nurse/profile') ? 'text-blue-600' : ''}`}
              fill={isActive('/nurse/profile') ? 'currentColor' : 'none'}
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
            <span className="text-xs font-medium">Profile</span>
          </Link>
        </div>
      </div>
    </nav>
  );
};

export default NurseBottomNavigation;

